import { Card, BlockStack, Text, Checkbox, Box, List } from '@shopify/polaris';
import { useCallback } from 'react';
import { StaggeredList, AccentBorderCard } from '../ui';
import { BRAND_COLORS } from '../../theme/colors';

/**
 * T020: ConsentStep — Step 3 of the onboarding wizard.
 * Displays data field list, consent checkbox gating the Start scan button (FR-005, FR-006).
 */
interface ConsentStepProps {
  consentGiven: boolean;
  onConsentChange: (checked: boolean) => void;
  onNext: () => void;
  onBack: () => void;
}

const DATA_FIELDS = [
  'Product titles and descriptions',
  'Product images and alt text',
  'Prices and variants',
  'Tags, product types, and categories',
  'Barcodes, SKUs, and identifiers',
  'Vendor and brand information',
] as const;

export function ConsentStep({
  consentGiven,
  onConsentChange,
  onNext: _onNext,
  onBack: _onBack,
}: Readonly<ConsentStepProps>) {
  const handleConsentChange = useCallback(
    (checked: boolean) => {
      onConsentChange(checked);
    },
    [onConsentChange],
  );

  return (
    <StaggeredList staggerDelay={0.06}>
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingLg">
            Transparency &amp; Consent
          </Text>

          <Text as="p" variant="bodyMd">
            To score your products, Zelium needs read-only access to the following data:
          </Text>

          <Box
            background="bg-surface-secondary"
            padding="400"
            borderRadius="200"
          >
            <List>
              {DATA_FIELDS.map((field) => (
                <List.Item key={field}>{field}</List.Item>
              ))}
            </List>
          </Box>

          <AccentBorderCard borderColor="var(--z-score-good)">
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                Your data is safe
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Zelium only reads your product data — we never modify, delete, or share it.
                Your data is processed securely and is never used for training AI models.
              </Text>
            </BlockStack>
          </AccentBorderCard>

          <Checkbox
            label={
              <span style={{ color: BRAND_COLORS.ctaBlue }}>
                I consent to Zelium reading my product data for AI discoverability scoring
              </span>
            }
            checked={consentGiven}
            onChange={handleConsentChange}
          />
        </BlockStack>
      </Card>
    </StaggeredList>
  );
}
